/**
 * Sync engine: push local changes, then pull the server's. Safe to run any time; it is
 * idempotent and conflict rules live in @hexling/core (last write wins, deletes win ties).
 */
import { incomingWins, mergeRecords, pendingChanges, type SyncRecord } from "@hexling/core";
import type { SyncPullResponse } from "@hexling/schema";
import type { Api } from "../lib/api";
import { COLLECTIONS, type Collection, type LocalStore } from "../storage/types";
import type { UserRepo } from "./user";

const LAST_PUSH = "sync.lastPushedAt";
const CURSOR = "sync.cursor";
const LAST_SYNC = "sync.lastSyncedAt";

export interface SyncResult {
  pushed: number;
  pulled: number;
  at: string;
}

export class SyncEngine {
  private running: Promise<SyncResult> | null = null;

  constructor(
    private readonly store: LocalStore,
    private readonly user: UserRepo,
    private readonly api: Api,
    private readonly now: () => Date = () => new Date(),
  ) {}

  /** Run a sync. Concurrent calls share the same run. */
  sync(): Promise<SyncResult> {
    this.running ??= this.run().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  async lastSyncedAt(): Promise<string | null> {
    return (await this.store.getMeta<string>(LAST_SYNC)) ?? null;
  }

  /** Forget sync position (e.g. after signing in as a different account). Local data is kept. */
  async reset(): Promise<void> {
    await this.store.setMeta(LAST_PUSH, null);
    await this.store.setMeta(CURSOR, null);
  }

  private async run(): Promise<SyncResult> {
    const startedAt = this.now().toISOString();
    const lastPushedAt = (await this.store.getMeta<string>(LAST_PUSH)) ?? null;

    const push: Record<string, unknown> = {};
    let pushed = 0;
    for (const c of COLLECTIONS) {
      const pending = pendingChanges(await this.store.all<SyncRecord>(c), lastPushedAt);
      push[c] = pending;
      pushed += pending.length;
    }
    const profile = await this.user.profile();
    // Only push a profile the witch has actually edited (a fresh default is dated 1970).
    if (profile.updatedAt > (lastPushedAt ?? new Date(0).toISOString())) {
      push.profile = profile;
      pushed++;
    }
    if (pushed > 0) await this.api.send("POST", "/api/sync", push);
    await this.store.setMeta(LAST_PUSH, startedAt);

    const cursor = (await this.store.getMeta<string>(CURSOR)) ?? null;
    const pull = await this.api.get<SyncPullResponse>(
      `/api/sync${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    );
    let pulled = 0;
    for (const c of COLLECTIONS) pulled += await this.merge(c, pull[c] as SyncRecord[]);
    if (
      pull.profile &&
      incomingWins(
        { id: "p", updatedAt: profile.updatedAt, deletedAt: null },
        { id: "p", updatedAt: pull.profile.updatedAt, deletedAt: null },
      )
    ) {
      await this.user.replaceProfile(pull.profile);
      pulled++;
    }
    await this.store.setMeta(CURSOR, pull.cursor);
    await this.store.setMeta(LAST_SYNC, this.now().toISOString());
    return { pushed, pulled, at: startedAt };
  }

  private async merge(c: Collection, incoming: SyncRecord[]): Promise<number> {
    if (!incoming.length) return 0;
    const { records, changedIds } = mergeRecords(await this.store.all<SyncRecord>(c), incoming);
    const changed = new Set(changedIds);
    await this.store.putMany(
      c,
      records.filter((r) => changed.has(r.id)),
    );
    return changedIds.length;
  }
}
